import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import "../../styles/DashboardHome.css";
import "../../styles/Tables.css";
import "../../styles/GuidedRegisters.css";
import "../../styles/EMSAuditTrail.css";
import { downloadEmsAuditLogCsvApi, fetchEmsAuditLogApi } from "../../lib/api";

export default function EMSAuditTrail() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    module_key: "",
    action: "",
    actor: "",
    date_from: "",
    date_to: "",
  });

  const loadEntries = async (f = filters) => {
    try {
      setLoading(true);
      const data = await fetchEmsAuditLogApi(f);
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.message || "Failed to load audit trail");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await downloadEmsAuditLogCsvApi(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ems_audit_log.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
      toast.success("Audit trail exported");
    } catch (err) {
      toast.error(err?.message || "Failed to export audit trail");
    } finally {
      setExporting(false);
    }
  };

  const setField = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = async () => {
    await loadEntries(filters);
  };

  const clearFilters = async () => {
    const next = { module_key: "", action: "", actor: "", date_from: "", date_to: "" };
    setFilters(next);
    await loadEntries(next);
  };

  const toDetailsText = (details) => {
    if (!details) return "-";
    if (typeof details === "string") return details || "-";
    try {
      return JSON.stringify(details);
    } catch {
      return "-";
    }
  };

  return (
    <div className="dashboard-page ems-audit-page guided-register-page">
      <div className="dashboard-header ems-audit-header">
        <div>
          <p className="guided-register-eyebrow">Read-only system record</p>
          <h1>EMS Audit Trail</h1>
          <p className="subtitle ems-audit-subtitle">A read-only record of approvals and decisions across the EMS, used to support traceability and audit review.</p>
        </div>
        <div className="dashboard-header-actions">
          <button className="btn-secondary" onClick={handleExportCsv} disabled={loading || exporting}>
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      <section className="card guided-register-guidance">
        <div>
          <p className="guided-register-eyebrow">How to use this record</p>
          <h3>Search the system history; do not edit it</h3>
          <p>Entries are created automatically when an EMS workflow records an approval or decision. Use the filters to narrow the history, then export the relevant results for management review or an external audit.</p>
        </div>
        <div className="guided-register-guidance-grid">
          <div><strong>Module and action</strong><span>Search for a particular module or event type, such as an approval request or approval decision.</span></div>
          <div><strong>Person and period</strong><span>Use actor and date filters to identify who made a change and when it occurred.</span></div>
          <div><strong>Traceability</strong><span>Use the item ID, details and event hash as the permanent record; these fields cannot be changed.</span></div>
        </div>
      </section>
      <div className="table-card ems-audit-card">
        <div className="filter-bar ems-audit-filter-bar">
          <label className="settings-field">
            <span>Module key</span>
            <input placeholder="e.g. internal_audits" value={filters.module_key} onChange={(e) => setField("module_key", e.target.value)} />
          </label>
          <label className="settings-field">
            <span>Action</span>
            <input placeholder="e.g. approved" value={filters.action} onChange={(e) => setField("action", e.target.value)} />
          </label>
          <label className="settings-field">
            <span>Actor contains</span>
            <input placeholder="e.g. manager name" value={filters.actor} onChange={(e) => setField("actor", e.target.value)} />
          </label>
          <label className="settings-field">
            <span>Date from</span>
            <input type="date" value={filters.date_from} onChange={(e) => setField("date_from", e.target.value)} />
          </label>
          <label className="settings-field">
            <span>Date to</span>
            <input type="date" value={filters.date_to} onChange={(e) => setField("date_to", e.target.value)} />
          </label>
          <div className="settings-actions ems-audit-filter-actions">
            <button className="btn-secondary" onClick={applyFilters} disabled={loading}>Apply</button>
            <button className="btn-secondary" onClick={clearFilters} disabled={loading}>Clear</button>
          </div>
        </div>
        <div className="ems-audit-table-wrap">
          <table>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Actor</th>
              <th>Action</th>
              <th>Module</th>
              <th>Item ID</th>
              <th>Details</th>
              <th>Event Hash</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="table-empty">Loading...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={7} className="table-empty">No audit entries match the selected filters yet. EMS approval activity will appear here automatically.</td></tr>
            ) : (
              entries.map((e) => (
                <tr key={e.id || `${e.timestamp}-${e.item_id}`}>
                  <td>{e.timestamp || "-"}</td>
                  <td>{e.actor || "-"}</td>
                  <td>{e.action || "-"}</td>
                  <td>{e.module_name || e.module_key || "-"}</td>
                  <td>{e.item_id || "-"}</td>
                  <td>{toDetailsText(e.details)}</td>
                  <td>{e.event_hash || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
